import { gsap } from "gsap";

/**
 * Project Info Card Controller
 * Handles:
 * - Smooth entrance transition with organic blur decode & micro-stagger
 * - Hidden on load, appears after a delay (data-card-delay, ms)
 * - Random position inside the viewport every time it appears
 * - Hides on scroll down, reappears on scroll up
 * - Mouse & touch drag-and-drop with screen bounds clamping
 * - Smooth morphing minimize & expand ("crecimiento xulo y smooth")
 */

export function initProjectCard() {
   const card = document.querySelector('.js-project-card');
   if (!card) return;

   const inner = card.querySelector('.m-project-card__inner') || card;
   const handle = card.querySelector('.js-card-handle') || card;
   const pill = card.querySelector('.js-card-pill');
   const content = card.querySelector('.js-card-content');

   let isDragging = false;
   let isAnimating = false;
   let startX = 0;
   let startY = 0;
   let initialLeft = 0;
   let initialTop = 0;
   let hasMoved = false;
   let lastScrollY = window.scrollY;

   const ENTRY_DELAY = parseInt(card.dataset.cardDelay || '2500', 10);
   const SCROLL_TOLERANCE = 8;
   const HEADER_OFFSET = 80;
   const EDGE_MARGIN = 16;
   let canShow = false;

   // 1. Random position inside the viewport (new spot every time it appears)
   function setRandomPosition() {
      const cardRect = card.getBoundingClientRect();
      const cardWidth = cardRect.width || 321;
      const cardHeight = cardRect.height || 480;

      const maxLeft = Math.max(EDGE_MARGIN, window.innerWidth - cardWidth - EDGE_MARGIN);
      const maxTop = Math.max(HEADER_OFFSET, window.innerHeight - cardHeight - EDGE_MARGIN);

      const left = EDGE_MARGIN + Math.random() * (maxLeft - EDGE_MARGIN);
      const top = HEADER_OFFSET + Math.random() * (maxTop - HEADER_OFFSET);

      card.style.left = `${Math.round(left)}px`;
      card.style.top = `${Math.round(top)}px`;
   }

   function showCard() {
      if (card.classList.contains('is-visible')) return;
      setRandomPosition();
      card.classList.add('is-visible');
      card.removeAttribute('aria-hidden');
   }

   function hideCard() {
      if (!card.classList.contains('is-visible')) return;
      card.classList.remove('is-visible');
      card.setAttribute('aria-hidden', 'true');
   }

   card.setAttribute('aria-hidden', 'true');
   setRandomPosition();

   // Hidden on load, appears after a few seconds
   setTimeout(() => {
      canShow = true;
      showCard();
   }, ENTRY_DELAY);

   function onDragStart(e) {
      if (isAnimating) return;
      // Don't drag if clicking directly on an interactive link/button
      if (e.target.closest('a, button, input')) return;

      isDragging = true;
      hasMoved = false;

      const clientX = e.type.startsWith('touch') ? e.touches[0].clientX : e.clientX;
      const clientY = e.type.startsWith('touch') ? e.touches[0].clientY : e.clientY;

      startX = clientX;
      startY = clientY;

      const rect = card.getBoundingClientRect();
      initialLeft = rect.left;
      initialTop = rect.top;

      document.addEventListener('mousemove', onDragMove, { passive: false });
      document.addEventListener('mouseup', onDragEnd);
      document.addEventListener('touchmove', onDragMove, { passive: false });
      document.addEventListener('touchend', onDragEnd);
   }

   function onDragMove(e) {
      if (!isDragging) return;

      const clientX = e.type.startsWith('touch') ? e.touches[0].clientX : e.clientX;
      const clientY = e.type.startsWith('touch') ? e.touches[0].clientY : e.clientY;

      const deltaX = clientX - startX;
      const deltaY = clientY - startY;

      if (!hasMoved) {
         if (Math.abs(deltaX) > 6 || Math.abs(deltaY) > 6) {
            hasMoved = true;
            card.classList.add('is-dragging');
         } else {
            return;
         }
      }

      e.preventDefault(); // Prevent scrolling while dragging

      let newLeft = initialLeft + deltaX;
      let newTop = initialTop + deltaY;

      // Clamp within viewport
      const cardRect = card.getBoundingClientRect();
      const maxLeft = window.innerWidth - cardRect.width - 12;
      const maxTop = window.innerHeight - cardRect.height - 12;

      newLeft = Math.max(12, Math.min(maxLeft, newLeft));
      newTop = Math.max(75, Math.min(maxTop, newTop)); // 75px prevents overlap with header

      card.style.left = `${newLeft}px`;
      card.style.top = `${newTop}px`;
   }

   function onDragEnd() {
      if (!isDragging) return;
      isDragging = false;
      card.classList.remove('is-dragging');

      document.removeEventListener('mousemove', onDragMove);
      document.removeEventListener('mouseup', onDragEnd);
      document.removeEventListener('touchmove', onDragMove);
      document.removeEventListener('touchend', onDragEnd);

      if (hasMoved) {
         setTimeout(() => {
            hasMoved = false;
         }, 80);
      } else {
         hasMoved = false;
      }
   }

   // Clamp within viewport on resize
   function clampCardToViewport() {
      if (isAnimating) return;
      const cardRect = card.getBoundingClientRect();
      const maxLeft = Math.max(12, window.innerWidth - cardRect.width - 12);
      const maxTop = Math.max(75, window.innerHeight - cardRect.height - 12);

      let currLeft = parseFloat(card.style.left) || 12;
      let currTop = parseFloat(card.style.top) || 75;

      currLeft = Math.max(12, Math.min(maxLeft, currLeft));
      currTop = Math.max(75, Math.min(maxTop, currTop));

      card.style.left = `${currLeft}px`;
      card.style.top = `${currTop}px`;
   }

   window.addEventListener('resize', clampCardToViewport);

   // Drag listeners attached to entire card
   card.addEventListener('mousedown', onDragStart);
   card.addEventListener('touchstart', onDragStart, { passive: false });

   // 2. Scroll controller: hides on scroll down, reappears (somewhere else) on scroll up
   function handleScroll() {
      const currentScrollY = window.scrollY;
      const delta = currentScrollY - lastScrollY;

      if (Math.abs(delta) < SCROLL_TOLERANCE) return;
      lastScrollY = currentScrollY;

      if (isDragging || isAnimating || !canShow) return;

      if (delta > 0 && currentScrollY > HEADER_OFFSET) {
         hideCard();
      } else if (delta < 0) {
         showCard();
      }
   }

   window.addEventListener('scroll', handleScroll, { passive: true });

   // Hide while the next-project transition is running
   document.addEventListener('project:leave', () => {
      canShow = false;
      hideCard();
   });

   window.addEventListener('pageshow', (e) => {
      if (!e.persisted) return;
      canShow = true;
      showCard();
   });

   // Helper: Measure the exact natural height required when minimized at the same width
   function getPillNaturalDimensions(fixedW) {
      const prevDisplay = pill.style.display;
      const prevVisibility = pill.style.visibility;
      const prevPosition = pill.style.position;
      const prevWidth = pill.style.width;
      const prevHeight = pill.style.height;

      pill.style.display = 'flex';
      pill.style.position = 'absolute';
      pill.style.visibility = 'hidden';
      pill.style.width = `${fixedW}px`;
      pill.style.height = 'auto';

      const rect = pill.getBoundingClientRect();
      const naturalW = fixedW;
      const naturalH = Math.max(60, Math.ceil(rect.height));

      pill.style.display = prevDisplay;
      pill.style.visibility = prevVisibility;
      pill.style.position = prevPosition;
      pill.style.width = prevWidth;
      pill.style.height = prevHeight;

      return { width: naturalW, height: naturalH };
   }

   // 3. Minimize & Expand Smooth Transitions
   function minimizeCard() {
      if (isAnimating) return;
      isAnimating = true;
      card.classList.add('is-animating');

      const startW = card.offsetWidth;
      const startH = card.offsetHeight;

      // Keep width unchanged (same width as expanded card), measure height
      const { width: targetW, height: targetH } = getPillNaturalDimensions(startW);

      // Prepare pill for morph
      pill.style.display = 'flex';
      pill.style.position = 'absolute';
      pill.style.top = '0';
      pill.style.left = '0';
      pill.style.width = '100%';
      pill.style.height = '100%';
      gsap.set(pill, { opacity: 0 });

      // Lock card dimensions & inner width for clean morph without text reflow
      card.style.overflow = 'hidden';
      card.style.width = `${startW}px`;
      card.style.maxWidth = `${startW}px`;
      card.style.height = `${startH}px`;
      inner.style.width = `${startW}px`;

      const tl = gsap.timeline({
         onComplete: () => {
            card.classList.add('m-project-card--minimized');
            card.classList.remove('is-animating');
            inner.style.display = 'none';
            inner.style.width = '';
            pill.style.display = 'flex';
            pill.style.opacity = '1';
            card.style.overflow = '';
            card.style.width = `${targetW}px`;
            card.style.maxWidth = `${targetW}px`;
            card.style.height = `${targetH}px`;
            card.setAttribute('aria-expanded', 'false');
            isAnimating = false;
         }
      });

      // 1. Height scales down smoothly to minimized pill height (width remains identical)
      tl.to(card, {
         height: targetH,
         duration: 0.38,
         ease: "power2.inOut"
      }, 0);

      // 2. Full content fades out smoothly
      tl.to(inner, {
         opacity: 0,
         duration: 0.2,
         ease: "power2.out"
      }, 0);

      // 3. Pill title appears simultaneously poco a poco
      tl.to(pill, {
         opacity: 1,
         duration: 0.28,
         delay: 0.1,
         ease: "power2.out"
      }, 0);
   }

   function expandCard() {
      if (isAnimating) return;
      isAnimating = true;
      card.classList.add('is-animating');

      const startW = card.offsetWidth;
      const startH = card.offsetHeight;
      const isMobile = window.innerWidth <= 640;
      // Strictly bounded width: 321px on desktop, never stretching across the screen
      const targetW = isMobile ? Math.min(321, window.innerWidth - 32) : 321;

      // Measure target full card height with targetW strictly enforced
      inner.style.display = 'block';
      inner.style.opacity = '0';
      inner.style.width = `${targetW}px`;
      card.style.width = `${targetW}px`;
      card.style.maxWidth = `${targetW}px`;
      card.style.height = 'auto';

      const targetH = card.offsetHeight;

      // Reset for animation start
      card.style.width = `${startW}px`;
      card.style.maxWidth = `${targetW}px`;
      card.style.height = `${startH}px`;
      card.style.overflow = 'hidden';
      pill.style.display = 'flex';
      pill.style.opacity = '1';

      // Boundary check: ensure expanding does not push card off-screen
      const currentLeft = parseFloat(card.style.left) || 16;
      const currentTop = parseFloat(card.style.top) || 95;
      let targetLeft = currentLeft;
      let targetTop = currentTop;

      if (currentLeft + targetW > window.innerWidth - 16) {
         targetLeft = Math.max(16, window.innerWidth - targetW - 16);
      }
      if (currentTop + targetH > window.innerHeight - 16) {
         targetTop = Math.max(75, window.innerHeight - targetH - 16);
      }

      // Unified timeline: box grows smoothly while pill dissolves and content appears "a la vez poco a poco"
      const tl = gsap.timeline({
         onComplete: () => {
            card.classList.remove('m-project-card--minimized');
            card.classList.remove('is-animating');
            pill.style.display = 'none';
            inner.style.display = 'block';
            inner.style.width = '';
            card.style.overflow = '';
            card.style.width = `${targetW}px`;
            card.style.maxWidth = `${targetW}px`;
            card.style.height = 'auto';
            card.style.left = `${targetLeft}px`;
            card.style.top = `${targetTop}px`;
            card.setAttribute('aria-expanded', 'true');
            isAnimating = false;
         }
      });

      // 1. Box dimensions & position grow smoothly to 321px
      tl.to(card, {
         width: targetW,
         maxWidth: targetW,
         height: targetH,
         left: targetLeft,
         top: targetTop,
         duration: 0.44,
         ease: "power2.out"
      }, 0);

      // 2. Pill dissolves smoothly
      tl.to(pill, {
         opacity: 0,
         duration: 0.18,
         ease: "power2.out"
      }, 0);

      // 3. Header & content appear simultaneously "a la vez poco a poco"
      tl.fromTo(inner,
         { opacity: 0, y: 8 },
         { opacity: 1, y: 0, duration: 0.38, delay: 0.08, ease: "power2.out" },
         0
      );
   }

   function toggleCard(e) {
      if (e) {
         e.stopPropagation();
         e.preventDefault();
      }
      if (hasMoved || isAnimating) return;

      const isCurrentlyMin = card.classList.contains('m-project-card--minimized');
      if (isCurrentlyMin) {
         expandCard();
      } else {
         minimizeCard();
      }
   }

   // Click on handle bar minimizes
   handle.addEventListener('click', (e) => {
      if (!hasMoved) {
         toggleCard(e);
      }
   });

   handle.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
         e.preventDefault();
         toggleCard(e);
      }
   });

   // Click on pill expands
   if (pill) {
      pill.addEventListener('click', (e) => {
         if (!hasMoved) {
            toggleCard(e);
         }
      });

      pill.addEventListener('keydown', (e) => {
         if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggleCard(e);
         }
      });
   }
}
